import { Box, Flex } from '@chakra-ui/react';
import { cn } from '@foam/buoy/src/lib/utils';
import { match } from 'ts-pattern';
import { Paragraph, Tooltip } from 'ui-components';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components';
import { VideoHover } from '~/components/VideoHover';
import { ContentImage } from '~/screens/SearchContent/components/ContentImage';
import { ASSET_TYPE, ISearchContentResult } from '~/searchApi';
import { isSafariBrowser } from '~/utils-ts';

import { singleContentStyle } from './styles';
import { getContentPreviewUrl, getIsStoryExpired } from './utils';

interface Props {
  content: ISearchContentResult;
  /* noRoot: If true, the content will apply root styles. Useful when checkbox styles are needed in the modal */
  noRoot?: boolean;
  onClick?: (contentId: string) => void;
}

export const SingleContent = ({ content, noRoot = false, onClick }: Props) => {
  const previewUrl = getContentPreviewUrl(content);

  const isStoryExpired = getIsStoryExpired(content);
  const tooltipLabel = isStoryExpired
    ? "Sorry, this content link isn't available from Instagram"
    : '';

  const isSafari = isSafariBrowser();

  return (
    <Tooltip label={tooltipLabel} hasArrow data-testid="content-tooltip">
      <Box
        sx={
          noRoot
            ? { height: singleContentStyle.root.height }
            : singleContentStyle.root
        }
        onClick={() => onClick?.(content.id)}
        data-testid="content-item"
        className="flex justify-center"
      >
        {match(content.type)
          .with(ASSET_TYPE.video, () => (
            <VideoHover
              src={content.assets.at(0)?.url ?? ''}
              poster={previewUrl}
              height={singleContentStyle.root.height}
              maxWidth="100%"
              width={isSafari ? undefined : '100%'}
              objectFit="cover"
              containerProps={{
                height: '100%',
                sx: singleContentStyle.image,
                className: cn({ 'flex justify-center': isSafari }),
              }}
              fallback={
                <ContentImage
                  draggable="false"
                  height={singleContentStyle.root.height}
                  maxWidth="100%"
                  width={isSafari ? undefined : '100%'}
                  objectFit="cover"
                  src={previewUrl}
                  alt={content.caption}
                  sx={singleContentStyle.image}
                  fallbackImageProps={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    paddingX: '4rem',
                    iconProps: { boxSize: 8 },
                  }}
                />
              }
            />
          ))
          .otherwise(() => (
            <ContentImage
              draggable="false"
              height={singleContentStyle.root.height}
              maxWidth="100%"
              width={isSafari ? undefined : '100%'}
              objectFit="cover"
              src={previewUrl}
              alt={content.caption}
              sx={singleContentStyle.image}
              fallbackImageProps={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                paddingX: '4rem',
                iconProps: { boxSize: 8 },
              }}
            />
          ))}
        <Flex
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="platform--container"
          sx={singleContentStyle.bottomBarContainer}
        >
          <Box sx={singleContentStyle.bottomBarContent}>
            <Paragraph
              size="sm"
              fontWeight="medium"
              sx={singleContentStyle.name}
            >
              {content.creator.fullName}
            </Paragraph>
            <ContentCardPlatformIcon
              width={4}
              height={4}
              platform={content.platform}
              sx={singleContentStyle.platformIcon}
            />
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  );
};
